import React, { useEffect, useState } from "react";
import { DataGrid, gridClasses, GridMoreVertIcon } from "@mui/x-data-grid";
import {
  Tabs,
  Tab,
  Button,
  IconButton,
  Popover,
  MenuItem,
} from "@mui/material";
import dayjs from "dayjs";
import { TbFilterEdit } from "react-icons/tb";
import { HttpService } from "../../service/HttpService";
import FullScreenLoader from "../../components/Loader/FullScreenLoader";
import DateTimeModal from "../Schedule/ScheduleListAdd/DateTimeModal";
import RankingModal from "../../components/Model/RankingModal";
import { toast } from "react-toastify";
import EditPlantMappingModal from "../../components/Model/EditPlantMappingModal";

const PlantMapping = ({
  roomId: externalRoomId,
  harvested = false,
  start_harvested_date,
  end_harvested_date,
}) => {
  const [epicenterTab, setEpicenterTab] = useState(0);
  const [roomTab, setRoomTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(15);
  const [rowCount, setRowCount] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [activeTabType, setActiveTabType] = useState("epicenter"); // "epicenter" or "room"
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);
  const [openRankingModal, setOpenRankingModal] = useState(false);
  const [rankingMappingId, setRankingMappingId] = useState(null);
  const [rankingValues, setRankingValues] = useState({
    rootsRanking: "",
    plantHealthRanking: "",
    developmentRanking: "",
  });
  const [savingRanking, setSavingRanking] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editMappingData, setEditMappingData] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [cloneId, setCloneId] = useState("");

  // mapping of tab index → epicenter value from backend
  const tabTypes = ["E1", "E2", "E3"];

  const httpService = new HttpService();

  // Fetch rooms for room tabs
  const fetchRooms = async () => {
    setRoomsLoading(true);
    try {
      const res = await httpService.get("rooms?sortBy=name&sortOrder=ASC");
      const roomsData = res?.data?.data || [];
      const filteredRooms = roomsData
        .filter(
          (room) =>
            room.name && /^room\s([1-9][0-9]?)$/i.test(room.name.trim()),
        )
        .sort((a, b) => {
          const numA = parseInt(a.name.match(/\d+/)?.[0] || 0, 10);
          const numB = parseInt(b.name.match(/\d+/)?.[0] || 0, 10);
          return numA - numB;
        });

      setRooms(filteredRooms);
      setRoomTab(0);
      // No fetchMappings() call here
    } catch (e) {
      console.error("Error fetching rooms:", e);
      setRooms([]);
    }
    setRoomsLoading(false);
  };

  const fetchMappings = async () => {
    setLoading(true);
    try {
      let apiUrl = `mapping?search=${encodeURIComponent(search || "")}`;
      // &page=${page + 1}&pageSize=${pageSize}`;
      apiUrl += `&epicenter=${tabTypes[epicenterTab]}`;
      if (externalRoomId) {
        apiUrl += `&room_id=${externalRoomId}`;
      } else if (rooms.length && rooms[roomTab]) {
        apiUrl += `&room_id=${rooms[roomTab]?.id}`;
      }
      if (harvested) {
        apiUrl += `&harvested=true`;
      }
      if (start_harvested_date) {
        apiUrl += `&start_harvested_date=${start_harvested_date}`;
      }
      if (end_harvested_date) {
        apiUrl += `&end_harvested_date=${end_harvested_date}`;
      }
      const res = await httpService.get(apiUrl);
      const list = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data?.data)
          ? res.data?.data
          : [];
      const pagination = res?.data?.pagination;
      const normalized = list.map((it, idx) => ({
        id: it.id ?? idx,
        metrc_id: it.matric_id || it.metrc_id || it.clone_id || "",
        bucket_id: it.bucket_id || "",
        bucketid: it.bucketid || "",
        clone_id: it.tray_clone_id || "",
        cloneid: it.cloneid || "",
        date_of_mapping: it.date_of_mapping || it.mapping_date || it.date || "",
        employee_name: it.employee_name || "",
        planting_date: it.planting_date || it.date_of_planting || "",
        epicenter: it.epicenter || "",
        room_id: it.room_id || "",
        room_name: it.room_name || "",
        root_ranking: it.root_ranking || "-",
        health_ranking: it.health_ranking || "-",
        development_ranking: it.development_ranking || "-",
        cultivar_name: it.cultivar_name || "",
        updated_mapping_date: it.updated_mapping_date || "",
      }));

      setRows(normalized);
      setRowCount(pagination?.total);
    } catch (e) {
      setRows([]);
      setRowCount(0);
    }
    setLoading(false);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  useEffect(() => {
    if (!externalRoomId) {
      fetchRooms();
    }
  }, [externalRoomId]);

  useEffect(() => {
    if (
      externalRoomId ||
      (rooms.length > 0 && rooms[0]?.name?.toLowerCase().startsWith("room"))
    ) {
      setRoomTab(0);
      fetchMappings();
    }
  }, [rooms, externalRoomId]);

  useEffect(() => {
    fetchMappings();
  }, [
    search,
    page,
    pageSize,
    epicenterTab,
    roomTab,
    rooms[roomTab]?.id,
    externalRoomId,
  ]);

  const hasMetrcId =
    rows.length === 0 ||
    rows.some(
      (row) =>
        row.metrc_id &&
        String(row.metrc_id).trim() !== "" &&
        String(row.metrc_id).trim() !== "-",
    );
  const hasCloneId =
    rows.length === 0 ||
    rows.some(
      (row) =>
        row.clone_id &&
        String(row.clone_id).trim() !== "" &&
        String(row.clone_id).trim() !== "-",
    );

  const columns = [
    ...(harvested
      ? []
      : [
          // {
          //   field: "action",
          //   headerName: "Action",
          //   headerAlign: "center",
          //   align: "center",
          //   sortable: false,
          //   width: 160,
          //   renderCell: (params) => (
          //     <>
          //       <IconButton
          //         onClick={(e) => {
          //           handleMenuClick(e);
          //           setMenuRowId(params?.row?.id);
          //         }}
          //       >
          //         <GridMoreVertIcon />
          //       </IconButton>
          //       {anchorEl && menuRowId === params?.row?.id && (
          //         <Popover
          //           className="popup-menu"
          //           open={Boolean(anchorEl)}
          //           anchorEl={anchorEl}
          //           onClose={handleCloseMenu}
          //           anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          //           transformOrigin={{ vertical: "top", horizontal: "right" }}
          //         >
          //           <MenuItem
          //             onClick={() => {
          //               setRankingMappingId(params?.row?.id);
          //               setCloneId(params?.row?.clone_id);
          //               setRankingValues({
          //                 rootsRanking: params?.row?.root_ranking || "",
          //                 plantHealthRanking: params?.row?.health_ranking || "",
          //                 developmentRanking:
          //                   params?.row?.development_ranking || "",
          //               });
          //               setOpenRankingModal(true);
          //               handleCloseMenu();
          //             }}
          //             className="menu-list-items"
          //           >
          //             <div className="menu-list-items-text">Ranking Option</div>
          //           </MenuItem>
          //           <MenuItem
          //             onClick={() => {
          //               setEditMappingData({
          //                 id: params?.row?.id,
          //                 epicenter: params?.row?.epicenter,
          //                 bucket_id: params?.row?.bucket_id,
          //                 bucketid: params?.row?.bucketid,
          //                 metrc_id: params?.row?.metrc_id,
          //                 room_id: params?.row?.room_id,
          //                 clone_id: params.row?.clone_id
          //                   ? params.row.clone_id
          //                   : "-",
          //                 cloneid: params?.row?.cloneid || "",
          //               });
          //               setOpenEditModal(true);
          //               handleCloseMenu();
          //             }}
          //             className="menu-list-items"
          //           >
          //             <div className="menu-list-items-text">
          //               Edit Plant Mapping
          //             </div>
          //           </MenuItem>
          //         </Popover>
          //       )}
          //     </>
          //   ),
          // },
        ]),
    ...(hasMetrcId
      ? [{ field: "metrc_id", headerName: "METRC ID", flex: 1, minWidth: 270 }]
      : []),
    { field: "bucket_id", headerName: "Bucket ID", flex: 1, minWidth: 170 },
    ...(hasCloneId
      ? [
          {
            field: "clone_id",
            headerName: "Clone ID",
            flex: 1,
            minWidth: 190,
            renderCell: (params) => (
              <div
                style={{
                  backgroundColor: params.row?.updated_mapping_date
                    ? "#ffd3ac59"
                    : "transparent",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  padding: "10px",
                }}
              >
                {params.value}
              </div>
            ),
          },
        ]
      : []),
    {
      field: "cultivar_name",
      headerName: "Cultivar Name",
      flex: 1,
      minWidth: 170,
    },
    // {
    //   field: "date_of_mapping",
    //   headerName: "Date of Mapping",
    //   flex: 1,
    //   minWidth: 200,
    //   renderCell: (params) => (
    //     <div style={{ padding: "10px" }}>
    //       {params.row?.date_of_mapping
    //         ? dayjs(params.row.date_of_mapping).format("MM-DD-YYYY")
    //         : "-"}
    //     </div>
    //   ),
    // },
    { field: "employee_name", headerName: "EMP Name", flex: 1, minWidth: 170 },
    {
      field: "planting_date",
      headerName: "Planting Date",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <div style={{ padding: "10px" }}>
          {params.row?.updated_mapping_date
            ? dayjs(params.row.updated_mapping_date).format("MM-DD-YYYY")
            : params.row?.planting_date
              ? dayjs(params.row.planting_date).format("MM-DD-YYYY")
              : "-"}
        </div>
      ),
    },
    // { field: "room_name", headerName: "Room Name", flex: 1 },
    { field: "epicenter", headerName: "Epicenter", flex: 1, minWidth: 170 },
    // {
    //   field: "root_ranking",
    //   headerName: "Roots Ranking",
    //   flex: 1,
    //   minWidth: 200,
    //   renderCell: (params) => <span>{params.row?.root_ranking ?? "-"}</span>,
    // },
    // {
    //   field: "health_ranking",
    //   headerName: "Health Ranking",
    //   flex: 1,
    //   minWidth: 200,
    //   renderCell: (params) => <span>{params.row?.health_ranking ?? "-"}</span>,
    // },
    // {
    //   field: "development_ranking",
    //   headerName: "Development Ranking",
    //   flex: 1,
    //   minWidth: 250,
    //   renderCell: (params) => (
    //     <span>{params.row?.development_ranking ?? "-"}</span>
    //   ),
    // },
  ];

  return (
    <>
      {(loading || roomsLoading) && <FullScreenLoader />}
      <div className="maintenance-view">
        {!externalRoomId && (
          <div className="page-title">
            <div className="title-wrap">
              <h1>Plant Mapping History</h1>
            </div>
          </div>
        )}

        {!externalRoomId && (
          <Tabs
            value={roomTab}
            onChange={(e, newValue) => {
              setRoomTab(newValue);
              setPage(0);
            }}
            variant="scrollable"
            scrollButtons="false"
            sx={{
              backgroundColor: "#fffffc",
              ".MuiTabs-flexContainer": {
                justifyContent: "space-between",
                display: "flex",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#00A272",
                height: "3px",
              },
              "& .MuiTab-root": {
                position: "relative",
                color: "#A1A2A1",
                fontWeight: 400,
                fontSize: "16px",
                textTransform: "none",
                padding: "12px 20px",
                marginRight: "8px",
                "&:not(.Mui-selected)::after": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: "3px",
                  backgroundColor: "#C4C4C4",
                },
                "&.Mui-selected": {
                  color: "#00A272 !important",
                  fontWeight: 600,
                  backgroundColor: "#E6F6F1",
                  borderRadius: "4px 4px 0 0",
                },
              },
            }}
            className="mb-3"
          >
            {rooms
              .filter(
                (room) =>
                  room.name && room.name.toLowerCase().startsWith("room"),
              )
              .map((room) => (
                <Tab key={room.id} label={room.name} />
              ))}
          </Tabs>
        )}

        <Tabs
          value={epicenterTab}
          onChange={(e, newValue) => {
            setEpicenterTab(newValue);
            setPage(0);
          }}
          sx={{
            backgroundColor: "#fffffc",
            "& .MuiTabs-indicator": {
              backgroundColor: "#00A272",
              height: "3px",
            },
            "& .MuiTab-root": {
              position: "relative",
              color: "#A1A2A1",
              fontWeight: 400,
              fontSize: "16px",
              textTransform: "none",
              padding: "12px 20px",
              marginRight: "8px",
              "&:not(.Mui-selected)::after": {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: "3px",
                backgroundColor: "#C4C4C4",
              },
              "&.Mui-selected": {
                color: "#00A272 !important",
                fontWeight: 600,
                backgroundColor: "#E6F6F1",
                borderRadius: "4px 4px 0 0",
              },
            },
          }}
        >
          <Tab label="Epicenter 1" />
          <Tab label="Epicenter 2" />
          <Tab label="Epicenter 3" />
        </Tabs>

        <div className="table-filter-wrap d-flex justify-content-between pb-0 pt-4">
          <div className="header_search-wrap">
            <div className="header_search_bar common_content d-flex">
              <div className="input-group input_group">
                <button className="btn search-btn search_btn">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 21 21"
                    fill="none"
                  >
                    <circle
                      cx="9.98856"
                      cy="9.98856"
                      r="8.98856"
                      stroke="#495057"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16.2402 16.7071L19.7643 20.222"
                      stroke="#495057"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <input
                  type="search"
                  className="form-control form_control"
                  placeholder="Search..."
                  aria-label="Search"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div>
          <div
            style={{
              height: `${rows?.length > 0 ? "" : "200px"}`,
              width: "100%",
              marginTop: 12,
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              rowCount={rowCount}
              // pageSizeOptions={[15, 50, 100]}
              // paginationModel={{ pageSize, page }}
              // paginationMode="server"
              // onPaginationModelChange={({ page, pageSize }) => {
              //   setPage(page);
              //   setPageSize(pageSize);
              // }}
              getRowHeight={() => "auto"}
              sx={{
                border: "1px solid #E0E0E0",
                [`& .${gridClasses.cell}`]: { py: 0.5 },
              }}
              autoHeight
              showCellVerticalBorder
              showColumnVerticalBorder
              hideFooter
            />
          </div>
        </div>
      </div>

      <RankingModal
        open={openRankingModal}
        onClose={() => setOpenRankingModal(false)}
        defaultValues={rankingValues}
        saving={savingRanking}
        cloneid={cloneId}
        onSubmit={async (vals) => {
          try {
            if (!rankingMappingId) return;
            setSavingRanking(true);
            await httpService.put(`mapping/ranking/${rankingMappingId}`, {
              root_ranking: vals.rootsRanking,
              health_ranking: vals.plantHealthRanking,
              development_ranking: vals.developmentRanking,
            });
            toast.success("Ranking saved", { position: "top-right" });
            setOpenRankingModal(false);
            setRankingMappingId(null);
            setCloneId("");
            setRankingValues({
              rootsRanking: "",
              plantHealthRanking: "",
              developmentRanking: "",
            });
            fetchMappings();
          } catch (e) {
            toast.error("Failed to save ranking", { position: "top-right" });
          } finally {
            setSavingRanking(false);
          }
        }}
      />
      <EditPlantMappingModal
        open={openEditModal}
        data={editMappingData}
        saving={savingEdit}
        onClose={() => setOpenEditModal(false)}
        onSubmit={async (vals) => {
          try {
            if (!vals?.clone_id?.id) {
              toast.error("Clone ID is required", { position: "top-right" });
              return;
            }
            setSavingEdit(true);
            await httpService.put(`mapping/${editMappingData.id}`, {
              clone_id: vals?.clone_id?.id,
            });
            toast.success("Plant mapping updated", { position: "top-right" });
            setOpenEditModal(false);
            setEditMappingData(null);
            fetchMappings();
          } catch (e) {
            // toast.error("Failed to update plant mapping", {
            //   position: "top-right",
            // });
          } finally {
            setSavingEdit(false);
          }
        }}
      />
    </>
  );
};

export default PlantMapping;
